import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';

import { ApiError, ERROR_CODES } from '@/shared/api/http';
import { internalRedirect } from '@/modules/auth/redirectTarget';

import { forgetSession, isLeavingSession, leaveSignedOut } from './session';

/**
 * A session can end while a screen is open: it reached its limit (12 hours, or 8 without use;
 * 30 and 14 days with "Recordarme"), the account was suspended, or a support session's grant was
 * revoked (ISO-25.5, `AM-134` point 11). There is no single place in the console that "owns" the
 * session: whichever query or mutation is the one to notice gets a plain 401. Before this hook, that
 * screen was left behind with empty lists and errors (guía única, capítulo 8, regla 8.8; AL-01 del
 * diagnóstico). This watches every query and mutation of the three shells (console, portal,
 * platform) for a 401 and sends the person where it belongs:
 *
 * - `support_access_expired`: back to `/platform/support-access`, with the notice, as before;
 * - `session_expired` or `unauthenticated`: to `/login`, with the page it was on as the `redirect`
 *   to come back to once inside. It leaves the way "Cerrar sesión" does (`AM-879`): the screen first,
 *   the cache after, so the shell does not re-read a session the API already refused.
 *
 * A sign-out in progress already produces 401s on purpose and is not an expiry.
 */
export function useSessionExpiry(): void {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const current = `${location.pathname}${location.searchStr}`;

  useEffect(() => {
    let handled = false;
    const handle = (error: unknown) => {
      if (handled || !(error instanceof ApiError) || error.status !== 401 || isLeavingSession()) {
        return;
      }
      if (error.errorCode === ERROR_CODES.supportAccessExpired) {
        handled = true;
        forgetSession(queryClient);
        void navigate({ to: '/platform/support-access', search: { expired: true } });
        return;
      }
      if (
        error.errorCode === ERROR_CODES.sessionExpired ||
        error.errorCode === ERROR_CODES.unauthenticated
      ) {
        handled = true;
        const redirect = internalRedirect(current);
        void leaveSignedOut(queryClient, () =>
          navigate({ to: '/login', search: redirect === undefined ? {} : { redirect } }),
        );
      }
    };

    const unsubscribeQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.action.type === 'error') {
        handle(event.action.error);
      }
    });
    const unsubscribeMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type === 'updated' && event.mutation.state.status === 'error') {
        handle(event.mutation.state.error);
      }
    });

    return () => {
      unsubscribeQueries();
      unsubscribeMutations();
    };
  }, [queryClient, navigate, current]);
}
